import { Injectable } from '@angular/core';
import { AuthService } from '@core/authentication/auth.service';
import { BehaviorSubject, Observable } from 'rxjs';

export interface MenuTag {
  // Background Color
  color: string;
  value: string;
}

export interface MenuChildrenItem {
  children?: MenuChildrenItem[];
  name: string;
  route: string;
  report: string;
  roles: string[];
  showDivider?: boolean;
  type: 'label' | 'link' | 'sub' | 'extLink' | 'extTabLink' | 'envTabLink';
}

export interface Menu {
  badge?: MenuTag;
  children?: MenuChildrenItem[];
  icon: string;
  label?: MenuTag;
  report: string;
  roles: string[];
  name: string;
  route: string;
  isNameVisible: boolean;
  type: 'label' | 'link' | 'sub' | 'extLink' | 'extTabLink' | 'envTabLink';
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menu$: BehaviorSubject<Menu[]> = new BehaviorSubject<Menu[]>([]);

  private filteredMenu$: BehaviorSubject<Menu[]> = new BehaviorSubject<Menu[]>([]);

  constructor(private authService: AuthService) {
    this.menu$.subscribe(items => {
      this.authService.getRoles().subscribe(roles => {
        const filtered = structuredClone(items).filter(this.roleFilter(roles));
        this.filteredMenu$.next(filtered);
      });
    });
  }

  add(menu: Menu) {
    const tmpMenu = this.menu$.value;
    tmpMenu.push(menu);
    this.menu$.next(tmpMenu);
  }

  getAll(): Observable<Menu[]> {
    return this.menu$.asObservable();
  }

  getFilteredMenu(): Observable<Menu[]> {
    return this.filteredMenu$.asObservable();
  }

  getMenuItemName(routeArr: string[]): string {
    return this.getMenuLevel(routeArr)[routeArr.length - 1];
  }

  getMenuLevel(routeArr: string[]): string[] {
    let tmpArr = [];
    this.menu$.value.forEach(item => {
      // breadth-first-traverse -modified
      let unhandledLayer = [{ item, parentNamePathList: [], realRouteArr: [] }];
      while (unhandledLayer.length > 0) {
        let nextUnhandledLayer: any[] = [];
        for (const ele of unhandledLayer) {
          const eachItem = ele.item;
          const currentNamePathList = this.deepcopyJsonObj(ele.parentNamePathList).concat(
            eachItem.name
          );
          const currentRealRouteArr = this.deepcopyJsonObj(ele.realRouteArr).concat(eachItem.route);
          // compare the full Array for expandable
          const cond = this.routeEqual(routeArr, currentRealRouteArr);
          if (cond) {
            tmpArr = currentNamePathList;
            break;
          }

          const isLeafCond = this.isLeafItem(eachItem);
          if (!isLeafCond) {
            const { children } = eachItem;
            const wrappedChildren = children?.map(child => ({
              item: child,
              parentNamePathList: currentNamePathList,
              realRouteArr: currentRealRouteArr,
            }));
            nextUnhandledLayer = nextUnhandledLayer.concat(wrappedChildren);
          }
        }
        unhandledLayer = nextUnhandledLayer;
      }
    });
    return tmpArr;
  }

  recursMenuForTranslation(menu: Menu[] | MenuChildrenItem[], namespace: string) {
    for (const menuItem of menu) {
      menuItem.name = `${namespace}.${menuItem.name}`;
      if (menuItem.children && menuItem.children.length > 0) {
        this.recursMenuForTranslation(menuItem.children, menuItem.name);
      }
    }
  }

  recursMenuForRouting(menu: Menu[] | MenuChildrenItem[], parent: Menu | MenuChildrenItem | null) {
    for (const menuItem of menu) {
      let route = parent?.route ?? '' !== '' ? parent?.route : menuItem.route;
      route += menuItem.route !== '' && (parent?.route ?? '') !== '' ? '/' + menuItem.route : '';
      menuItem.route = route ?? '';

      if ((!menuItem.roles || menuItem.roles.length === 0) && parent?.roles) {
        menuItem.roles = parent.roles;
      }

      if (menuItem.children && menuItem.children.length > 0) {
        this.recursMenuForRouting(menuItem.children, structuredClone(menuItem));
      }
    }
  }

  reset() {
    this.menu$.next([]);
  }

  set(menu: Menu[]): Observable<Menu[]> {
    this.menu$.next(menu);
    return this.menu$.asObservable();
  }

  private deepcopyJsonObj(jobj: any): any {
    /// / deepcop object-could-be-jsonized
    return JSON.parse(JSON.stringify(jobj));
  }

  private isLeafItem(item: MenuChildrenItem): boolean {
    /// / if a menuItem is leaf
    const cond0 = item.route === undefined;
    const cond1 = item.children === undefined;
    const cond2 = !cond1 && item.children?.length === 0;
    return cond0 || cond1 || cond2;
  }

  private jsonObjEqual(jobj0: any, jobj1: any): boolean {
    /// / if two objects-could-be-jsonized equal
    const cond = JSON.stringify(jobj0) === JSON.stringify(jobj1);
    return cond;
  }

  private routeEqual(routeArr: Array<string>, realRouteArr: Array<string>): boolean {
    /// / if routeArr equals realRouteArr(after remove empty-route-element)
    let realRouteArrTmp = this.deepcopyJsonObj(realRouteArr);
    realRouteArrTmp = realRouteArrTmp.filter(r => r !== '');
    return this.jsonObjEqual(routeArr, realRouteArrTmp);
  }

  private roleFilter(userRoles: string[] | undefined) {
    return item => {
      if (item.children && item.children.length > 0) {
        item.children = item.children.filter(this.roleFilter(userRoles));
      }

      if (item.roles) {
        let hasRole = false;
        for (const roleName of item.roles) {
          hasRole = !!userRoles?.includes(roleName);
          if (hasRole) {
            break;
          }
        }
        return hasRole ? item : false;
      }

      return item;
    };
  }
}
